import {useContractStatus, usePersonalData, useHostName} from 'presentation/hooks';
import {useAccountInformation, useChangeDataProfile} from 'presentation/pages/private/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

import {ProfileMenuItemStatus} from '../components/ProfileMenu/constants';
import {ProfileMenuItemConfig} from '../components/ProfileMenu/types';
import {AccordionStateEnum} from '../store/formAccordionStore/types';
import {formatMasks, formatText} from 'presentation/utils';
import {getAddressDescription} from '../utils/getAddressDescription';
import {validateCellPhone} from 'presentation/utils/validateCellPhone';
import {translations} from 'presentation/translations-new';
import {useGetBeneficiaryBankDataQuery} from '../queries/useGetBeneficiaryBankDataQuery';
import {useValidateRefundBankQuery} from '../queries/useValidateRefundBankQuery';
import {BankValidityEnum} from 'domain/usecases/private/refund/request/get-validate-bank';
import {useGetFavoriteAddressesQuery} from '../FavoriteAddresses/queries/useGetFavoriteAddressesQuery';
import {
	emailRegexValidation,
	FacialBiometricEnum,
	FeaturesEnum,
	ProfileEnum,
} from 'presentation/constants';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useFacialBiometricFeatureFlag} from '../../hooks/useFacialBiometricFeatureFlag';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {TutorialStepsId} from '../FavoriteAddresses/components/FavoriteAddressTutorials/constants';
import {FacialBiometricsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {useShareAccessFeatureFlag} from '../../hooks/useShareAccessFeatureFlag';
const {
	changeData: {contact, emptyAlert, address, bank, facialBiometricGuardiansRegisteredMessage},
} = translations['pt-br'].privateFlow.profile;
const {loading, cellphone, email, registered, facialBiometric} = translations['pt-br'].commons;
const {changePassword, dataSharing} = translations['pt-br'].privateFlow.profile;

export function useProfileMenuItems() {
	const {currentPhone, currentEmail, userLocation, emptyLocation} = usePersonalData();
	const {
		canChangePassword,
		canChangeAddress,
		accesses,
		isHolder,
		odontoOnly,
		businessPlan,
		canChangeAccess,
	} = useAccountInformation();
	const {isValid, isCancelledAtFiveYears} = useContractStatus();
	const {isGndiSaoPaulo, isNotredameSaude} = useHostName();
	const {hasFacialBiometrics, guardians, isTea} = useFacialBiometricsStore(store => store);

	const navigate = useNavigate();

	const [isAlreadyRegisteredModalOpen, setIsAlreadyRegisteredModalOpen] = useState(false);

	const {isLoading: isBankDataLoading, isError, data} = useGetBeneficiaryBankDataQuery();

	const {handleChangeDataFocus} = useChangeDataProfile();

	const {isEnabled: isFacialBiometricEnabled} = useFacialBiometricFeatureFlag();
	const {isEnabled: isMultiAddressEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_MULTI_ADDRESS,
	);
	const {isEnabled: isRefundBankAndAgencyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_BANK_AND_AGENCY,
	);
	const {isEnabled: isShareAccessEnabled} = useShareAccessFeatureFlag();

	const {
		data: favoriteAddresses,
		isLoading: isFavoriteAddressesLoading,
		isFetched: isFavoriteAddressesFetched,
	} = useGetFavoriteAddressesQuery({enabled: isMultiAddressEnabled});

	const isGndiOrNotredame = isGndiSaoPaulo || isNotredameSaude;

	const showBankData = accesses.refund && isHolder && !odontoOnly && isGndiOrNotredame;

	const {data: bankValidation, isLoading: isBankValidationLoading} = useValidateRefundBankQuery({
		bankCode: data?.bank?.code ?? '',
		agencyCode: data?.agency ?? '',
		enabled: isRefundBankAndAgencyEnabled && showBankData && Boolean(data),
	});

	const isBankInvalid = bankValidation?.isValid === BankValidityEnum.INVALID;
	const isBankResolving = isBankDataLoading || isBankValidationLoading;
	const hasBankError = !isBankResolving && (!data || isBankInvalid);
	const bankStatus = hasBankError ? ProfileMenuItemStatus.ERROR : ProfileMenuItemStatus.SUCCESS;
	const bankErrorMessage = isBankInvalid
		? emptyAlert.bankInvalidMessage
		: emptyAlert.bankEmptyMessage;

	const emptyAddress =
		(isMultiAddressEnabled || (!isMultiAddressEnabled && emptyLocation)) &&
		!isFavoriteAddressesLoading &&
		!favoriteAddresses;

	const canOpenAddressAction = isValid || isCancelledAtFiveYears;

	const currentPhoneIsInvalid = !validateCellPhone(currentPhone);
	const currentEmailIsInvalid = !emailRegexValidation.test(currentEmail || '');

	const handleNavToChangePassword = () => {
		navigate(ProfileEnum.CHANGE_PASSWORD);
	};

	const addressDescription = getAddressDescription({
		contactsAddress: isMultiAddressEnabled ? undefined : userLocation,
		favoriteAddresses,
		isLoading: isFavoriteAddressesLoading,
		loadingLabel: loading,
	});

	const handleAddressClick = () => {
		if (!canOpenAddressAction) {
			return undefined;
		}

		if (isMultiAddressEnabled) {
			return () => navigate(ProfileEnum.FAVORITE_ADDRESSES_LIST);
		}

		return () => handleChangeDataFocus(AccordionStateEnum.ADDRESS, true);
	};

	const getBankDescription = (): string => {
		if (isBankDataLoading) return loading;

		if (isError) return bank.errorMessage ?? '';

		const accountNumber = `${data?.account ?? ''}${
			data?.accountDigit ? `-${data.accountDigit}` : ''
		}`;

		return `${formatText(data?.bank?.description)}, agência ${data?.agency}, conta ${accountNumber}`;
	};

	const hasGuardians = guardians.length > 0;
	const registeredGuardiansCount = guardians.filter(guardian => guardian.facialRegistered).length;
	const areAllGuardiansRegistered = hasGuardians && registeredGuardiansCount === guardians.length;
	const isFacialBiometricFullyRegistered =
		isTea && hasGuardians ? areAllGuardiansRegistered : hasFacialBiometrics;
	const facialBiometricDescription =
		isTea && hasGuardians
			? facialBiometricGuardiansRegisteredMessage
					.replace('{current}', String(registeredGuardiansCount))
					.replace('{total}', String(guardians.length))
			: registered;

	const REGISTRATION_DATA_ITEMS: ProfileMenuItemConfig[] = [
		{
			id: 'biometrics',
			shouldShow: isFacialBiometricEnabled && !odontoOnly,
			label: facialBiometric,
			description: facialBiometricDescription,
			iconName: 'facial-recognition',
			status: hasFacialBiometrics
				? ProfileMenuItemStatus.SUCCESS
				: ProfileMenuItemStatus.ERROR,
			errorMessage: emptyAlert.facialBiometricEmptyMessage,
			onClick: isFacialBiometricFullyRegistered
				? () => setIsAlreadyRegisteredModalOpen(true)
				: () => navigate(FacialBiometricEnum.FACIAL_BIOMETRIC),
			dataTagId: FacialBiometricsTaggingEnum.CLICK_CADASTRAR_BIOMETRIA_FACIAL_PERFIL,
		},
		{
			id: 'phone',
			shouldShow: true,
			label: cellphone,
			description: formatMasks('phone', currentPhone) || contact.withoutCellPhone,
			iconName: 'phone-android-filled',
			status: currentPhoneIsInvalid
				? ProfileMenuItemStatus.ERROR
				: ProfileMenuItemStatus.SUCCESS,
			errorMessage: emptyAlert.cellPhoneEmptyMessage,
			onClick:
				isValid || isCancelledAtFiveYears
					? () => handleChangeDataFocus(AccordionStateEnum.CELLPHONE, true)
					: undefined,
		},
		{
			id: 'email',
			shouldShow: true,
			label: email,
			description: currentEmail || contact.withoutEmail,
			iconName: 'mail-filled',
			status: currentEmailIsInvalid
				? ProfileMenuItemStatus.ERROR
				: ProfileMenuItemStatus.SUCCESS,
			errorMessage: emptyAlert.emailEmptyMessage,
			onClick:
				isValid || isCancelledAtFiveYears
					? () => handleChangeDataFocus(AccordionStateEnum.EMAIL, true)
					: undefined,
		},
		{
			id: 'address',
			shouldShow: canChangeAddress || isMultiAddressEnabled,
			dataTagId: TutorialStepsId.PROFILE_MENU_ADDRESS,
			label: address.addressesLabel,
			description: addressDescription,
			iconName: 'map',
			status: emptyAddress ? ProfileMenuItemStatus.ERROR : ProfileMenuItemStatus.SUCCESS,
			errorMessage: emptyAlert.addressEmptyMessage,
			onClick: handleAddressClick(),
		},
		{
			id: 'bank-data',
			shouldShow: showBankData,
			label: bank.cardTitle,
			description: getBankDescription(),
			iconName: 'account-balance-default',
			status: bankStatus,
			errorMessage: bankErrorMessage,
			onClick: isBankDataLoading
				? undefined
				: () => handleChangeDataFocus(AccordionStateEnum.BANK, true),
		},
	];

	const LOGIN_AND_PRIVACY_ITEMS: ProfileMenuItemConfig[] = [
		{
			id: 'password',
			shouldShow: canChangePassword,
			label: changePassword.title,
			description: changePassword.description,
			iconName: 'closed-padlock',
			onClick: handleNavToChangePassword,
		},
		{
			id: 'data-sharing',
			shouldShow: isShareAccessEnabled && canChangeAccess,
			label: dataSharing.title,
			description: dataSharing.menuDescription,
			iconName: 'person-search',
			onClick: () => navigate(ProfileEnum.DATA_SHARING),
		},
	];

	return {
		isFavoriteAddressesFetched,
		REGISTRATION_DATA_ITEMS,
		LOGIN_AND_PRIVACY_ITEMS,
		showB2BToggle: isGndiOrNotredame && businessPlan,
		isAlreadyRegisteredModalOpen,
		setIsAlreadyRegisteredModalOpen,
	};
}
